import { AxiosError } from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { client } from "../../api/client";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Dropdown from "../../components/ui/dropdown";
import { Backdrop } from "../../components/ui/Modal";

interface Props {
  showConfirm: (config: {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    variant?: "primary" | "danger" | "warning";
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  showInfo: (cfg: any) => void;
  navigateTab: (tab: any) => void;
}

interface ImportOptions {
  supported_models: string[];
  max_file_size_bytes: number;
}

interface ImportResult {
  id: number;
  target_model: string;
  filename: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  status: string;
  error_message?: string;
}

interface SchemaField {
  name: string;
  required: boolean;
  type: string;
  example: string;
  help_text: string;
}

interface SchemaResponse {
  model: string;
  fields: SchemaField[];
}

const MODEL_HINT: Record<string, string> = {
  metadata: "Upload first — other tables reference experiment_id.",
  lnp: "Main data. il_smiles_mol / morgan_fp computed server-side via RDKit.",
  head: "Head building blocks.",
  linker: "Linker building blocks.",
};

const DataImportTab: React.FC<Props> = ({ showConfirm, showInfo }) => {
  const [options, setOptions] = useState<ImportOptions | null>(null);
  const [modelName, setModelName] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<string[] | null>(null);

  const [metadataMethod, setMetadataMethod] = useState<"csv" | "manual">("csv");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    experiment_id: "",
    title: "",
    author: "",
    doi: "",
    journal: "",
    abstract: "",
  });
  const [isCreatingPub, setIsCreatingPub] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [schema, setSchema] = useState<SchemaField[] | null>(null);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const [metadataList, setMetadataList] = useState<
    { experiment_id: string; title: string }[]
  >([]);
  const [selectedMetadataId, setSelectedMetadataId] = useState<string>("");

  useEffect(() => {
    if (!modelName) {
      setSchema(null);
      return;
    }
    const loadSchema = async () => {
      setIsLoadingSchema(true);
      try {
        const resp = await client.get<SchemaResponse>(
          `/search/admin/import/schema/${modelName}/`,
        );
        setSchema(resp.data.fields);
      } catch (err) {
        setSchema(null);
      } finally {
        setIsLoadingSchema(false);
      }
    };
    loadSchema();
  }, [modelName]);

  const loadOptions = useCallback(async () => {
    try {
      const resp = await client.get<ImportOptions>("/search/admin/import/");
      setOptions({
        ...resp.data,
        supported_models: ["lnp", "metadata"],
      });
      if (!modelName) {
        setModelName("lnp");
      }
    } catch {
      // Non-fatal — fall back to a sensible default set.
      setOptions({
        supported_models: ["lnp", "metadata"],
        max_file_size_bytes: 20 * 1024 * 1024,
      });
      if (!modelName) {
        setModelName("lnp");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMetadata = useCallback(async () => {
    try {
      const resp = await client.get("/search/admin/metadata/");
      if (resp.status === 200) {
        if (Array.isArray(resp.data)) {
          setMetadataList(resp.data);
        } else if (resp.data && resp.data.results) {
          setMetadataList(resp.data.results);
        }
      }
    } catch (err: any) {
      console.error("Failed to load metadata list", err);
    }
  }, []);

  useEffect(() => {
    loadOptions();
    loadMetadata();
  }, [loadOptions, loadMetadata]);

  const handleCreatePublication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createFormData.experiment_id) {
      showInfo({ title: "Error", message: "Experiment ID is required.", variant: "warning" });
      return;
    }
    setIsCreatingPub(true);
    try {
      const resp = await client.post("/search/admin/metadata/", createFormData);
      const newMeta = resp.data;
      setMetadataList((prev) => [
        ...prev,
        { experiment_id: newMeta.experiment_id, title: newMeta.title },
      ]);
      setSelectedMetadataId(newMeta.experiment_id);
      setIsCreateModalOpen(false);
      setCreateFormData({
        experiment_id: "",
        title: "",
        author: "",
        doi: "",
        journal: "",
        abstract: "",
      });
      showInfo({ title: "Success", message: "Publication created successfully.", variant: "success" });
    } catch (err: any) {
      const msg = err.response?.data?.experiment_id?.[0] || "Failed to create publication.";
      showInfo({ title: "Error", message: msg, variant: "warning" });
    } finally {
      setIsCreatingPub(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const runImport = async () => {
    if (!file || !modelName) return;
    setIsSubmitting(true);
    setError(null);
    setConflicts(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("model_name", modelName);
      if (modelName === "lnp" && selectedMetadataId) {
        formData.append("metadata_id", selectedMetadataId);
      }
      const resp = await client.post<ImportResult>(
        "/search/admin/import/upload/",
        formData,
      );

      if (resp.data.invalid_rows > 0) {
        showConfirm({
          title: "Validation Errors Found",
          message: `Uploaded with ${resp.data.valid_rows} valid rows and ${resp.data.invalid_rows} invalid rows. There are missing required fields or validation errors. Would you like to review and edit them in 'My Uploads' or cancel the task?`,
          confirmLabel: "Keep & Edit",
          cancelLabel: "Cancel Task",
          variant: "warning",
          onConfirm: () => {
            resetForm();
          },
          onCancel: async () => {
            try {
              await client.delete(`/search/admin/import/${resp.data.id}/`);
              showInfo({
                title: "Canceled",
                message: "Task canceled and upload removed.",
                variant: "info",
              });
              resetForm();
            } catch (delErr) {
              console.error(delErr);
              showInfo({
                title: "Error",
                message: "Failed to cancel task.",
                variant: "warning",
              });
              resetForm();
            }
          },
        });
      } else {
        showInfo({
          title: "Import Uploaded",
          message: `Ready to confirm. ${resp.data.valid_rows} valid rows found. Job ID: ${resp.data.id}`,
          variant: "success",
        });
        resetForm();
      }
    } catch (err) {
      const axiosErr = err as AxiosError<{
        detail?: string;
        conflicts?: string[];
      }>;
      const detail = axiosErr.response?.data?.detail ?? "Import failed.";
      const conflictList = axiosErr.response?.data?.conflicts;
      setError(detail);
      if (conflictList && conflictList.length > 0) {
        setConflicts(conflictList);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError("Select a CSV file first.");
      return;
    }
    if (!modelName) {
      setError("Select a target model.");
      return;
    }
    if (options && file.size > options.max_file_size_bytes) {
      const mb = options.max_file_size_bytes / (1024 * 1024);
      setError(`File exceeds ${mb} MB limit.`);
      return;
    }
    showConfirm({
      title: `Import into ${modelName}`,
      message: `Upload "${file.name}" (${(file.size / 1024).toFixed(1)} KB) into the ${modelName} table? Any primary-key collision will reject the entire upload.`,
      confirmLabel: "Upload",
      variant: "primary",
      onConfirm: runImport,
    });
  };

  const hint = modelName ? MODEL_HINT[modelName] : "";

  return (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        {/* Left Column: Upload Form Container */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          {/* Top-Level Tabs */}
          <div className="px-5 pt-4 border-b border-gray-200">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              Upload CSV to database
            </h3>
            <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
              {options?.supported_models.map((name) => (
                <li className="mr-2" key={name}>
                  <button
                    type="button"
                    onClick={() => {
                      setModelName(name);
                      setError(null);
                      setConflicts(null);
                    }}
                    className={`inline-block px-4 py-3 border-b-2 rounded-t-lg transition-colors ${
                      modelName === name
                        ? "text-blue-600 border-blue-600"
                        : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {name === "metadata" ? "Publication" : name.toUpperCase()}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="p-5">
            <p className="mb-6 text-sm text-gray-600">
              Append-only. If any primary-key value already exists the whole upload
              is rolled back — edit the CSV and try again.
              {hint && <span className="block mt-1 text-gray-500">{hint}</span>}
            </p>

            {modelName === "metadata" && (
              <div className="mb-6 flex p-1 bg-gray-100 rounded-lg w-fit">
                <button
                  type="button"
                  onClick={() => setMetadataMethod("csv")}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-shadow ${
                    metadataMethod === "csv"
                      ? "bg-white shadow text-gray-900"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Upload CSV
                </button>
                <button
                  type="button"
                  onClick={() => setMetadataMethod("manual")}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-shadow ${
                    metadataMethod === "manual"
                      ? "bg-white shadow text-gray-900"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Direct Input (Modal)
                </button>
              </div>
            )}

            {modelName === "metadata" && metadataMethod === "manual" ? (
              <div className="py-8 mt-2 text-center border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                <h4 className="text-sm font-medium text-gray-900">Create Publication Manually</h4>
                <p className="mt-1 text-sm text-gray-500">
                  Open the form modal to create a single publication without a CSV file.
                </p>
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(true)}
                  className="mt-5 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
                >
                  Open Form
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label
                    htmlFor="file-input"
                    className="block mb-1 text-sm font-medium text-gray-700"
                  >
                    Upload CSV or Excel file
                  </label>
                  <input
                    ref={fileInputRef}
                    id="file-input"
                    type="file"
                    accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 file:px-4 file:py-2 file:mr-4 file:text-sm file:font-medium file:text-blue-700 file:bg-blue-50 file:border-0 file:cursor-pointer hover:file:bg-blue-100"
                  />
                  {options && (
                    <p className="mt-1 text-xs text-gray-500">
                      Max {(options.max_file_size_bytes / (1024 * 1024)).toFixed(0)}{" "}
                      MB.
                    </p>
                  )}
                </div>

                {modelName === "lnp" && (
                  <div className="mt-6">
                    <label
                      htmlFor="metadata-select"
                      className="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Publication (Optional)
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Dropdown
                          id="metadata-dropdown"
                          options={metadataList.map((m) => ({
                            value: m.experiment_id,
                            label: `[${m.experiment_id}] ${m.title || "No Title"}`,
                          }))}
                          value={selectedMetadataId}
                          onChange={setSelectedMetadataId}
                          placeholder="a publication"
                          emptyLabel="-- No publication selected --"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setModelName("metadata");
                          setMetadataMethod("manual");
                        }}
                        className="px-4 py-2.5 text-sm font-medium text-blue-700 bg-white border border-blue-700 rounded-lg hover:bg-blue-50 whitespace-nowrap transition-colors"
                      >
                        Create new
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Select a publication to apply to all LNPs in this CSV. If an LNP
                      row already contains an experiment_id, it will override this
                      selection.
                    </p>
                  </div>
                )}

                {error && (
                  <div
                    className="p-3 mt-6 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
                    role="alert"
                  >
                    {error}
                    {conflicts && conflicts.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-red-900">
                          {conflicts.length} conflict(s) (click to expand)
                        </summary>
                        <ul className="pl-4 mt-2 text-xs list-disc">
                          {conflicts.slice(0, 30).map((c) => (
                            <li key={c} className="break-all">
                              {c}
                            </li>
                          ))}
                          {conflicts.length > 30 && (
                            <li>…and {conflicts.length - 30} more.</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
                  <button
                    type="submit"
                    disabled={isSubmitting || !file || !modelName}
                    className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                  >
                    {isSubmitting ? "Uploading..." : "Upload CSV"}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>

        {/* Right Column: Schema Table */}
        <div className="w-full">
          {schema && (
            <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-900">
              CSV Schema for "{modelName}"
            </h4>
            {isLoadingSchema && <LoadingSpinner variant="inline" />}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-700 border-collapse">
              <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="px-3 py-2 font-medium">Field</th>
                  <th className="px-3 py-2 font-medium">Required</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Example</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {schema
                  .filter((f) => f.required)
                  .map((f) => (
                    <tr key={f.name} className="bg-white hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs font-bold text-blue-700 break-all">
                        {f.name}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Yes
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{f.type}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 break-all">
                        {f.example}
                      </td>
                    </tr>
                  ))}
                {schema.some((f) => !f.required) && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-2 text-center bg-gray-50 border-t border-gray-200"
                    >
                      <button
                        type="button"
                        onClick={() => setShowOptional(!showOptional)}
                        className="text-xs font-medium text-gray-600 hover:text-gray-900 flex items-center justify-center w-full focus:outline-none"
                      >
                        <svg
                          className={`w-4 h-4 mr-1 transition-transform ${showOptional ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {showOptional
                          ? "Hide Optional Fields"
                          : `Show Optional Fields (${schema.filter((f) => !f.required).length})`}
                      </button>
                    </td>
                  </tr>
                )}
                {showOptional &&
                  schema
                    .filter((f) => !f.required)
                    .map((f) => (
                      <tr
                        key={f.name}
                        className="bg-gray-50 opacity-90 hover:opacity-100 hover:bg-gray-100 transition-colors"
                      >
                        <td className="px-3 py-2 font-mono text-xs text-gray-600 break-all">
                          {f.name}
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                            No
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {f.type}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-400 break-all">
                          {f.example}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {isCreateModalOpen && (
        <Backdrop onClose={() => setIsCreateModalOpen(false)} size="lg">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Create new Publication</h3>
          <form onSubmit={handleCreatePublication} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Experiment ID (Required)</label>
              <input required type="text" className="w-full mt-1 border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none" value={createFormData.experiment_id} onChange={e => setCreateFormData({...createFormData, experiment_id: e.target.value})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Title (Optional)</label>
              <input type="text" className="w-full mt-1 border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none" value={createFormData.title} onChange={e => setCreateFormData({...createFormData, title: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Author (Optional)</label>
                <input type="text" className="w-full mt-1 border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none" value={createFormData.author} onChange={e => setCreateFormData({...createFormData, author: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">DOI (Optional)</label>
                <input type="text" className="w-full mt-1 border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none" value={createFormData.doi} onChange={e => setCreateFormData({...createFormData, doi: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Journal (Optional)</label>
              <input type="text" className="w-full mt-1 border border-gray-300 px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none" value={createFormData.journal} onChange={e => setCreateFormData({...createFormData, journal: e.target.value})} />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setIsCreateModalOpen(false)} disabled={isCreatingPub} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={isCreatingPub} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">{isCreatingPub ? "Saving..." : "Create"}</button>
            </div>
          </form>
        </Backdrop>
      )}

      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center justify-center shadow-xl">
            <LoadingSpinner />
            <p className="mt-4 text-gray-700 font-medium">Uploading and parsing file...</p>
          </div>
        </div>
      )}
    </>
  );
};

export default DataImportTab;
